import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import WeatherWidget from '../components/WeatherWidget';

const { FiPlay, FiVideo, FiMapPin, FiEdit, FiArrowRight, FiUsers, FiHeart, FiStar, FiTv, FiMic, FiCamera, FiMusic, FiCalendar, FiAward } = FiIcons;

function Home() {
  useEffect(() => {
    // Load ZCal embed script
    const script = document.createElement('script');
    script.src = 'https://static.zcal.co/embed/v1/embed.js';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const creatorSpotlights = [
    {
      name: "Marcus",
      location: "Brooklyn",
      story: "From street art to digital canvas, I capture the soul of NYC through my lens."
    },
    {
      name: "Sofia",
      location: "Queens",
      story: "Every neighborhood has a rhythm, and I'm here to share the beat with the world."
    },
    {
      name: "James",
      location: "Manhattan",
      story: "The city never sleeps, and neither do the stories waiting to be told."
    }
  ];

  const boroughs = [
    {
      name: "Manhattan",
      description: "From Wall Street to Harlem, capturing the heartbeat of NYC's most iconic borough",
      neighborhoods: ["Times Square", "SoHo", "Greenwich Village", "Upper East Side", "Harlem", "Lower East Side"],
      color: "text-yellow"
    },
    {
      name: "Brooklyn",
      description: "Where creativity meets community - showcasing the borough's artistic renaissance",
      neighborhoods: ["Williamsburg", "DUMBO", "Park Slope", "Bushwick", "Crown Heights", "Red Hook"],
      color: "text-secondary-blue"
    },
    {
      name: "Queens",
      description: "The world's borough - celebrating diversity through food, culture, and stories",
      neighborhoods: ["Astoria", "Long Island City", "Flushing", "Jackson Heights", "Forest Hills", "Elmhurst"],
      color: "text-tertiary-blue"
    },
    {
      name: "The Bronx",
      description: "Home of hip-hop and authentic NYC culture - where legends are born",
      neighborhoods: ["South Bronx", "Fordham", "Hunts Point", "Concourse", "Tremont", "Belmont"],
      color: "text-accent-blue"
    },
    {
      name: "Staten Island",
      description: "NYC's hidden gem - featuring unique stories from the forgotten borough",
      neighborhoods: ["St. George", "Stapleton", "New Brighton", "Port Richmond", "Tottenville", "Great Kills"],
      color: "text-yellow"
    }
  ];

  const contentTypes = [
    {
      icon: FiTv,
      title: "Live Shows & Talk Shows",
      description: "Real conversations with NYC locals, artists, entrepreneurs, and community leaders sharing their authentic stories."
    },
    {
      icon: FiMic,
      title: "Podcast-Style Interviews",
      description: "Deep dives into the lives and experiences of New Yorkers from all walks of life, exploring what makes each story unique."
    },
    {
      icon: FiCamera,
      title: "Documentary Features",
      description: "Short-form documentaries highlighting NYC's hidden gems, local businesses, and community initiatives."
    },
    {
      icon: FiMusic,
      title: "Live Music & Performance",
      description: "Showcasing NYC's incredible music scene - from subway performers to established artists calling the city home."
    },
    {
      icon: FiCalendar,
      title: "Community Events",
      description: "Coverage of local festivals, street fairs, art shows, and neighborhood gatherings that make NYC special."
    },
    {
      icon: FiAward,
      title: "Local Business Spotlights",
      description: "Celebrating NYC's small businesses, family-owned restaurants, and entrepreneurs building community wealth."
    }
  ];

  const whoItsFor = [
    {
      icon: FiUsers,
      title: "NYC Locals",
      description: "Born and raised New Yorkers looking to discover new corners of their city and connect with their community."
    },
    {
      icon: FiHeart,
      title: "NYC Transplants",
      description: "People who've made NYC home and want to dive deeper into authentic local culture beyond tourist attractions."
    },
    {
      icon: FiStar,
      title: "Content Creators",
      description: "Local artists, performers, and storytellers seeking a platform to share their work with an engaged NYC audience."
    },
    {
      icon: FiMapPin,
      title: "Community Leaders",
      description: "Activists, organizers, and local leaders working to make positive change in their neighborhoods."
    }
  ];

  const quickLinks = [
    { name: 'Watch Live', path: '/watch', icon: FiPlay, description: 'Tune into live BoroughTV' },
    { name: 'VOD Library', path: 'https://vod.borough.tv/channel/66ddf45007db27257ddb436a/playlist/6706d6bfb42fe49b30fed261/', icon: FiVideo, description: 'Browse our video collection', external: true },
    { name: 'Things to Do', path: '/things-to-do', icon: FiMapPin, description: 'Explore NYC activities' },
    { name: 'Read Our Blog', path: 'https://blog.boroughtv.nyc', icon: FiEdit, description: 'Latest stories and updates', external: true }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Video Background */}
      <section className="relative overflow-hidden" style={{ height: '80vh', minHeight: '600px' }}>
        {/* Video Background */}
        <div className="absolute inset-0 w-full h-full">
          <div className="absolute inset-0 bg-black bg-opacity-60 z-10"></div>
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center center' }}
          >
            <source src="https://cdn.dyntube.net/play/use-s/data/BgGGXgDp0UGxpAa13qNEA/videos/8SciobdKB0W5IuUBWCJRQ/v1/mp4/pubsrv/720_l0ALZugvyki6MqIVEnW26Q.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>

        {/* Hero Content */}
        <div className="relative z-20 flex items-center justify-center h-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-white">
                NYC Made Entertainment
              </h1>
              <h2 className="text-xl md:text-2xl font-medium text-yellow mb-8">
                Made in NYC, BY NYC Locals
              </h2>
              <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto">
                Your Local TV Station for Authentic NYC Stories. From every borough, every block, every heartbeat of the city.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/watch"
                  className="bg-accent-blue hover:bg-accent-blue/80 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-300 flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiPlay} />
                  <span>Watch Live</span>
                </Link>
                <a
                  href="https://vod.borough.tv/channel/66ddf45007db27257ddb436a/playlist/6706d6bfb42fe49b30fed261/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-primary-blue hover:bg-primary-blue/80 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-300 flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiVideo} />
                  <span>Browse VOD</span>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is BoroughTV Section */}
      <section className="py-20 bg-dark-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 gradient-text">
              What is BoroughTV?
            </h2>
            <div className="max-w-4xl mx-auto space-y-6 text-lg text-gray-300">
              <p>
                BoroughTV is NYC's premier digital storytelling platform and local TV station, dedicated to amplifying authentic voices from all five boroughs. We're more than just entertainment - we're the intersection of street culture, creativity, and community where every New York story matters.
              </p>
              <p>
                Born from the streets of NYC, BoroughTV showcases the real experiences of local residents, artists, entrepreneurs, and community leaders. Our platform celebrates the diversity, creativity, and resilience that makes New York City the greatest city in the world.
              </p>
              <p>
                As your local TV station, we provide NYC-made entertainment that you won't find anywhere else - authentic, unfiltered, and proudly representing the five boroughs that make up the heart of New York.
              </p>
            </div>
          </motion.div>

          {/* Creator Stories Videos - Three Videos Grid */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="max-w-7xl mx-auto mb-16"
          >
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="video-container">
                <iframe
                  loading="lazy"
                  title="Gumlet video player - Creator Stories 1"
                  src="https://play.gumlet.io/embed/687a8d5e7193df23aca58592?background=false&autoplay=false&loop=false&disableControls=false"
                  style={{ border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
                />
              </div>
              <div className="video-container">
                <iframe
                  loading="lazy"
                  title="Gumlet video player - Creator Stories 2"
                  src="https://play.gumlet.io/embed/687aa7cdaa14b70eb1d4a64c?background=false&autoplay=false&loop=false&disableControls=false"
                  style={{ border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
                />
              </div>
              <div className="video-container md:col-span-2 lg:col-span-1 lg:mx-0 md:mx-auto md:max-w-md">
                <iframe
                  loading="lazy"
                  title="Gumlet video player - Creator Stories 3"
                  src="https://play.gumlet.io/embed/687aafc9aa14b70eb1d4f14c?background=false&autoplay=false&loop=false&disableControls=false"
                  style={{ border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
                />
              </div>
            </div>
          </motion.div>

          {/* Creator Spotlights */}
          <div className="grid md:grid-cols-3 gap-8">
            {creatorSpotlights.map((creator, index) => (
              <motion.div
                key={creator.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                className="bg-dark-bg p-6 rounded-lg border border-dark-border card-hover"
              >
                <blockquote className="text-gray-300 mb-4">
                  "{creator.story}"
                </blockquote>
                <footer className="text-accent-blue font-semibold">
                  I'm {creator.name} from {creator.location}. And BoroughTV is where my story lives.
                </footer>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 gradient-text">
              Who BoroughTV is For
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              BoroughTV serves the diverse community of New York City, connecting locals with authentic stories and experiences from their neighborhoods.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whoItsFor.map((audience, index) => (
              <motion.div
                key={audience.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-dark-card p-6 rounded-lg border border-dark-border card-hover text-center"
              >
                <SafeIcon icon={audience.icon} className="text-3xl text-accent-blue mb-4 mx-auto" />
                <h3 className="text-xl font-semibold mb-3">{audience.title}</h3>
                <p className="text-gray-400">{audience.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Boroughs We Cover */}
      <section className="py-20 bg-dark-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 gradient-text">
              The Five Boroughs We Cover
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From Manhattan to Staten Island, BoroughTV showcases authentic stories from every corner of New York City, celebrating the unique character of each borough.
            </p>
          </motion.div>
          <div className="space-y-12">
            {boroughs.map((borough, index) => (
              <motion.div
                key={borough.name}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8`}
              >
                <div className="lg:w-1/2">
                  <h3 className={`text-2xl md:text-3xl font-bold mb-4 ${borough.color}`}>
                    {borough.name}
                  </h3>
                  <p className="text-lg text-gray-300 mb-6">
                    {borough.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {borough.neighborhoods.map((neighborhood) => (
                      <span
                        key={neighborhood}
                        className="bg-dark-bg px-3 py-1 rounded-full text-sm text-gray-400 border border-dark-border"
                      >
                        {neighborhood}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="lg:w-1/2">
                  <div className="bg-dark-bg p-8 rounded-lg border border-dark-border">
                    <SafeIcon icon={FiMapPin} className={`text-4xl mb-4 ${borough.color}`} />
                    <p className="text-gray-300">
                      Discover authentic stories, local businesses, and community events that make {borough.name} unique. From hidden gems to neighborhood legends, we showcase what locals love about their borough.
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Types Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 gradient-text">
              NYC Made Entertainment & Content
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              As your local TV station, BoroughTV produces diverse content that captures the authentic spirit of New York City, by New Yorkers, for New Yorkers.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {contentTypes.map((content, index) => (
              <motion.div
                key={content.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-dark-card p-6 rounded-lg border border-dark-border card-hover"
              >
                <SafeIcon icon={content.icon} className="text-3xl text-accent-blue mb-4" />
                <h3 className="text-xl font-semibold mb-3">{content.title}</h3>
                <p className="text-gray-400">{content.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NYC Community Showcase with Calendar */}
      <section className="py-20 bg-dark-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 gradient-text">
              NYC Community Showcase
            </h2>
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-gray-300 mb-6">
                BoroughTV's NYC Community Showcase is a lively platform dedicated to celebrating the diverse and dynamic spirit of New York City. This initiative highlights local talent, events, and community-driven efforts, giving residents and organizations a unique opportunity to share their stories and achievements.
              </p>
              <p className="text-lg text-gray-300 mb-8">
                Ready to share your story with NYC? Schedule a discovery call with our team to discuss how we can feature your content, event, or community initiative on BoroughTV. We're passionate about elevating the voices and contributions that make NYC extraordinary.
              </p>
            </div>
          </motion.div>

          {/* Calendar Embed - Updated to new link */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="w-full max-w-[80%] mx-auto"
          >
            <div className="bg-white rounded-lg overflow-hidden shadow-2xl min-h-[600px]">
              <div
                className="zcal-inline-widget w-full h-full"
                dangerouslySetInnerHTML={{
                  __html: `<a href="https://zcal.co/i/ntaXl-FN" style="display: block;width: 100%;height: 600px;text-align: center;padding-top: 250px;color: #666;text-decoration: none;">Discovery Call - Schedule a meeting</a>`
                }}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl md:text-4xl font-bold text-center mb-12 gradient-text"
          >
            Explore BoroughTV
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickLinks.map((link, index) => (
              <motion.div
                key={link.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                {link.external ? (
                  <a
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-dark-card p-6 rounded-lg border border-dark-border card-hover group"
                  >
                    <SafeIcon icon={link.icon} className="text-2xl text-accent-blue mb-4" />
                    <h3 className="text-xl font-semibold mb-2 group-hover:text-accent-blue transition-colors">
                      {link.name}
                    </h3>
                    <p className="text-gray-400 mb-4">{link.description}</p>
                    <div className="flex items-center text-accent-blue">
                      <span className="text-sm font-medium">Explore</span>
                      <SafeIcon icon={FiArrowRight} className="ml-2 text-sm group-hover:translate-x-1 transition-transform" />
                    </div>
                  </a>
                ) : (
                  <Link
                    to={link.path}
                    className="block bg-dark-card p-6 rounded-lg border border-dark-border card-hover group"
                  >
                    <SafeIcon icon={link.icon} className="text-2xl text-accent-blue mb-4" />
                    <h3 className="text-xl font-semibold mb-2 group-hover:text-accent-blue transition-colors">
                      {link.name}
                    </h3>
                    <p className="text-gray-400 mb-4">{link.description}</p>
                    <div className="flex items-center text-accent-blue">
                      <span className="text-sm font-medium">Explore</span>
                      <SafeIcon icon={FiArrowRight} className="ml-2 text-sm group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Weather Preview */}
      <section className="py-20 bg-dark-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 gradient-text">
              NYC Weather
            </h2>
            <p className="text-gray-300">Stay updated with the latest conditions in the city</p>
          </motion.div>
          <WeatherWidget preview={true} />
        </div>
      </section>
    </div>
  );
}

export default Home;